import { calculateAmortization } from './calculations'

// ─── Types ──────────────────────────────────────────────────────────────────

export type TrueCostLoanType = 'interest_only' | 'amortizing'

export interface TrueCostInputs {
  loanAmount: number
  interestRate: number         // annual, e.g. 12 for 12%
  termMonths: number
  loanType: TrueCostLoanType
  originationPoints: number    // e.g. 2 for 2%
  flatFees: number             // dollar amount
  holdMonths: number           // expected hold period
  extensionFeePoints: number   // per extension period, e.g. 1 for 1%
  extensionPeriodMonths: number
  prepaymentPenalty: number    // e.g. 2 for 2%
}

export interface CostBreakdownItem {
  label: string
  amount: number
  percentage: number  // of total cost
  color: string
}

export interface TrueCostResult {
  totalInterest: number
  originationCost: number
  flatFees: number
  extensionCost: number
  prepaymentCost: number
  totalCost: number
  effectiveAnnualRate: number
  monthlyCost: number
  breakdown: CostBreakdownItem[]
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

// ─── Core calculation ───────────────────────────────────────────────────────

export function calculateTrueCost(inputs: TrueCostInputs): TrueCostResult {
  const {
    loanAmount,
    interestRate,
    termMonths,
    loanType,
    originationPoints,
    flatFees,
    holdMonths,
    extensionFeePoints,
    extensionPeriodMonths,
    prepaymentPenalty,
  } = inputs

  // 1. Origination cost
  const originationCost = round2(loanAmount * (originationPoints / 100))

  // 2. Interest calculation
  let totalInterest: number

  if (loanType === 'interest_only') {
    const monthlyInterest = round2(loanAmount * (interestRate / 100) / 12)
    totalInterest = round2(monthlyInterest * holdMonths)
  } else {
    // Amortizing: reuse existing amortization math
    // Calculate for the full hold period to get total interest paid
    const effectiveTermForCalc = Math.min(holdMonths, termMonths)
    const result = calculateAmortization(
      loanAmount,
      interestRate,
      termMonths,
      new Date(),
      'standard',
    )
    // Sum interest from schedule up to the hold month
    totalInterest = round2(
      result.schedule
        .slice(0, effectiveTermForCalc)
        .reduce((sum, row) => sum + row.interestPortion, 0),
    )

    // If hold extends past the original term, the remaining balance continues
    // accruing interest at the same rate (extension period, interest-only on remaining)
    if (holdMonths > termMonths) {
      const remainingBalance = result.schedule.length > 0
        ? result.schedule[Math.min(termMonths, result.schedule.length) - 1].remainingBalance
        : loanAmount
      const extensionMonths = holdMonths - termMonths
      const monthlyExtInterest = round2(remainingBalance * (interestRate / 100) / 12)
      totalInterest = round2(totalInterest + monthlyExtInterest * extensionMonths)
    }
  }

  // 3. Extension cost (only if holdMonths > termMonths)
  let extensionCost = 0
  if (holdMonths > termMonths && extensionPeriodMonths > 0) {
    const extensionsNeeded = Math.ceil((holdMonths - termMonths) / extensionPeriodMonths)
    extensionCost = round2(extensionsNeeded * loanAmount * (extensionFeePoints / 100))
  }

  // 4. Prepayment penalty (only if holdMonths < termMonths)
  let prepaymentCost = 0
  if (holdMonths < termMonths) {
    prepaymentCost = round2(loanAmount * (prepaymentPenalty / 100))
  }

  // 5. Total cost
  const totalCost = round2(totalInterest + originationCost + flatFees + extensionCost + prepaymentCost)

  // 6. Effective annual rate
  const effectiveAnnualRate = holdMonths > 0 && loanAmount > 0
    ? round2((totalCost / loanAmount) / (holdMonths / 12) * 100)
    : 0

  // 7. Monthly cost
  const monthlyCost = holdMonths > 0 ? round2(totalCost / holdMonths) : 0

  // 8. Breakdown
  const breakdownItems: Array<{ label: string; amount: number; color: string }> = [
    { label: 'Interest', amount: totalInterest, color: '#818cf8' },
    { label: 'Origination Points', amount: originationCost, color: '#34d399' },
    { label: 'Flat Fees', amount: flatFees, color: '#fbbf24' },
    { label: 'Extension Fees', amount: extensionCost, color: '#f87171' },
    { label: 'Prepayment Penalty', amount: prepaymentCost, color: '#fb923c' },
  ]

  const breakdown: CostBreakdownItem[] = breakdownItems
    .filter((item) => item.amount > 0)
    .map((item) => ({
      ...item,
      percentage: totalCost > 0 ? round2((item.amount / totalCost) * 100) : 0,
    }))

  return {
    totalInterest,
    originationCost,
    flatFees,
    extensionCost,
    prepaymentCost,
    totalCost,
    effectiveAnnualRate,
    monthlyCost,
    breakdown,
  }
}
